// @flow
import * as React from "react";
import moment from "moment";
import styled, { ThemeConsumer } from "styled-components";

import type { MapStateToProps } from "react-redux";
import type { State as ReduxState } from "../redux/reducer";

function zero(value: string | number) {
  if (typeof value !== "number") {
    return value;
  }

  return value < 10 ? `0${value}` : value;
}

export const mapStateToTime: MapStateToProps<*, *, *> = (
  state: ReduxState
) => ({
  time: state.time || Date.now()
});

type DurationProps = {
  start: number | moment$Moment,
  end: number | moment$Moment,
  unix?: boolean
};

export function timer(props: DurationProps) {
  const start = props.unix ? moment.unix(props.start) : moment(props.start);
  const end = props.unix ? moment.unix(props.end) : moment(props.end);

  const duration = moment.duration(end.diff(start));

  if (!duration) {
    return "00:00:00";
  }

  const days = duration.days() > 0 ? `${duration.days()}d ` : "";
  const hours = zero(Math.max(0, duration.hours()));
  const minutes = zero(Math.max(0, duration.minutes()));
  const seconds = zero(Math.max(0, duration.seconds()));

  const timer = `${days}${hours}:${minutes}:${seconds}`;

  return timer;
}

export function percentage(
  partial: number,
  total: number,
  props: { direction?: "from" | "to" }
) {
  const percentage = (100 * partial) / total;

  return props.direction === "from" ? percentage : 100 - percentage;
}

const ProgressCircle = styled.circle`
  transition: stroke-dashoffset 0.15s linear;
`;

type OffsetProps = {
  radius: number, // радиус
  progress: number // процент прогресса
};

function determineOffset({ radius, progress }: OffsetProps) {
  const strokeDasharray = Math.round(Math.PI * (radius * 2)); // = длина окружности

  // оффсет не округляем, а обрезаем все дробные кусочки,
  // чтобы не показывать заполненный на 100% круг раньше времени
  const strokeDashoffset = Math.trunc(
    ((100 - progress) / 100) * strokeDasharray
  );

  return { strokeDasharray, strokeDashoffset };
}

type TimerProps = {
  progress: number, // текущий процент
  width?: number, // ширина квадрата = диаметр круга без обвода
  strokeWidth?: number, // ширина обводки
  stroke?: string, // цвет круга прогресса
  className?: string, // класс ВСЕЙ svg
  full?: {
    className?: string, // имя класса (ну мало ли)
    stroke?: string // цвет подложки
  }
};

export const RadialTimer = ({
  width = 100,
  strokeWidth = 4,
  ...props
}: TimerProps) => {
  const center = width / 2;
  const radius = (width - strokeWidth) / 2;

  const { strokeDasharray, strokeDashoffset } = determineOffset({
    radius,
    progress: props.progress || 0
  });

  return (
    <ThemeConsumer>
      {theme => (
        <svg
          viewBox={`0 0 ${width} ${width}`}
          width={width + "px"}
          className={props.className}
        >
          {props.full != null && (
            <circle
              r={radius}
              cx={center}
              cy={center}
              strokeWidth={strokeWidth}
              fill="none"
              {...props.full}
            />
          )}
          <ProgressCircle
            r={radius}
            cx={center}
            cy={center}
            fill="none"
            stroke={props.stroke || theme.accent}
            // нам нужно повернуть этот круг, чтобы точка отсчета была сверху;
            // для этого мы должны дать координаты точки поворота. Считаем мы
            // от центра - значит, x и y должны совпадать с x и y центра
            transform={`rotate(-90 ${center} ${center})`}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
      )}
    </ThemeConsumer>
  );
};



// WEBPACK FOOTER //
// ./src/components/Timers.js